import { useState } from "react";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import TableListCampaign from "../../shared/components/organisms/TableListCampaign";
import NavbarFilterCampaign from "@/shared/components/organisms/NavbarFilterCampaign";
import LoadTable from "@/shared/components/organisms/LoadTable";
import { Page } from "@/shared/components/atoms/Page";
import { useDebounce } from "@/shared/hooks/debounce";
import { selectUser } from "@/app/auth/auth.slice";
import { FIELD_REQUIRED_MESSAGE } from "@/shared/utils/validations";
import { toasts } from "@/shared/utils/toasts";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import {
  useCreateCampaignsMutation,
  useGetCampaignsCatchmentQuery,
  useUpdateCampaignsMutation,
} from "./api/catchment.api";

const validationSchema = Yup.object().shape({
  name: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  description: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  social: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  inversion: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  clv: Yup.string().required(FIELD_REQUIRED_MESSAGE),
});

const initialValues = {
  name: "",
  description: "",
  social: "",
  inversion: "",
  clv: "",
  sex: false,
  birthday: false,
  phone: false,
  comments: false,
};

export const CampaignPage = () => {
  const user = useSelector(selectUser);
  const branch_id = user?.branch.id;
  const [searchCampaign, setSearchCampaign] = useState<string | null>(null);
  const [openCampaignForm, setOpenCampaignForm] = useState(false);
  const [page, setPage] = useState(1);
  const [formDataEdit, setFormDataEdit] = useState(null);
  const debounceSearch = useDebounce(searchCampaign, 500);
  const { t } = useTranslation();
  const [createCatchment, { isLoading: isLoadingCampaigns }] =
    useCreateCampaignsMutation();
  const [updateCatchment, { isLoading: isLoadingUpdateCampaigns }] =
    useUpdateCampaignsMutation();

  const optionsMenu = [
    {
      name: "Nueva Campaña",
      functionMenu: () => setOpenCampaignForm(true),
    },
  ];

  const handleCreateCampaign = async (values) => {
    try {
      await createCatchment({
        body: values,
      }).unwrap();
      setFormDataEdit(null);
    } catch (error) {
      throw error;
    }
  };
  const handleUpdateCampaign = async (values) => {
    try {
      await updateCatchment({
        body: values,
        campaignId: values.id,
      }).unwrap();
      setFormDataEdit(null);
    } catch (error) {
      throw error;
    }
  };

  const formik = useFormik({
    initialValues: formDataEdit
      ? {
          ...formDataEdit,
          social: formDataEdit.media,
          inversion: formDataEdit.cost,
          sex: formDataEdit.sex === 1 ? true : false,
          birthday: formDataEdit.birthdate === 1 ? true : false,
          phone: formDataEdit.phone === 1 ? true : false,
          comments: formDataEdit.comment === 1 ? true : false,
        }
      : initialValues,
    onSubmit: async (values, formikHelpers) => {
      try {
        if (formDataEdit) {
          await handleUpdateCampaign({
            ...values,
            branch_id,
            media: values.social,
            cost: values.inversion,
            sex: values.sex ? 1 : 0,
            birthdate: values.birthdate ? 1 : 0,
            phone: values.phone ? 1 : 0,
            comment: values.comments ? 1 : 0,
          });
        } else {
          await handleCreateCampaign({
            ...values,
            branch_id,
            state: 1,
            media_check: 1,
            date: 1,
            media: values.social,
            cost: values.inversion,
            sex: values.sex ? 1 : 0,
            birthdate: values.birthdate ? 1 : 0,
            phone: values.phone ? 1 : 0,
            comment: values.comments ? 1 : 0,
            code: Math.random().toString(36).substring(2, 15),
          });
        }
        setOpenCampaignForm(false);
        toasts.success("Éxito", "Campaña creada exitosamente");
        formikHelpers.resetForm();
        refetch();
      } catch (error) {
        console.error(error);
      }
    },
    validationSchema,
    enableReinitialize: true,
  });

  console.log("values", formik.values);

  const {
    data: campaignsQuery,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetCampaignsCatchmentQuery(
    {
      branch_id: branch_id,
      search: debounceSearch,
    },
    {
      skip: !branch_id,
    }
  );

  if (isLoading || isFetching) {
    //skeleton grande
    return <LoadTable />;
  }

  if (isError) {
    //skeleton grande
    return (
      <div className="flex justify-center items-center h-full text-center" />
    );
  }

  return (
    <Page title="Campañas" description="Administra tus campañas de Marketing">
      <div className="flex items-center justify-end mt-4 px-0">
        <NavbarFilterCampaign
          searchCampaign={searchCampaign}
          setSearchCampaign={setSearchCampaign}
          optionsMenu={optionsMenu}
        />
      </div>
      <TableListCampaign
        campaigns={campaignsQuery?.campaigns ?? []}
        branch_id={branch_id}
        setFormDataEdit={(values) => {
          setFormDataEdit(values);
          setOpenCampaignForm(true);
        }}
      />

      <DialogCreate
        OnclickCreate={formik.handleSubmit}
        OnclickCancel={() => {
          setOpenCampaignForm(false);
          setFormDataEdit(null);
          formik.resetForm();
        }}
        onClose={() => {
          setOpenCampaignForm(false);
          formik.resetForm();
          setFormDataEdit(null);
        }}
        isLoading={
          isLoadingCampaigns ||
          isLoadingUpdateCampaigns ||
          formik.isSubmitting ||
          formik.isValidating
        }
        tittleButtonCreate={
          formDataEdit ? t("general.edit") : t("general.create")
        }
        tittleButtonCancel={t("general.cancel")}
        open={openCampaignForm}
        setOpen={() => {
          setOpenCampaignForm(false);
          setFormDataEdit(null);
          formik.resetForm();
        }}
        tittle={t("assigned.views.campaign")}
        subTittle={t("assigned.views.create_campaign")}
      >
        <div className="mt-6 mx-2">
          <div className="space-y-12">
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
              <div className="flex items-center justify-center h-full w-full text-center">
                <div>
                  <h2 className="text-base font-semibold leading-7 text-gray-900 w-full">
                    {t("assigned.views.campaigns")}
                  </h2>
                </div>
              </div>

              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("assigned.views.name")}
                    span={t("general.mandatory")}
                    id="name"
                    name="name"
                    type="text"
                    placeholder={t("assigned.views.name")}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    error={formik.errors.name}
                    textError={formik.errors.name}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("assigned.views.description")}
                    span={t("general.mandatory")}
                    id="description"
                    name="description"
                    type="text"
                    placeholder={t("assigned.views.description")}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    error={formik.errors.description}
                    textError={formik.errors.description}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("assigned.views.comunication")}
                    span={t("general.mandatory")}
                    id="social"
                    name="social"
                    type="text"
                    placeholder={t("assigned.views.comunication")}
                    value={formik.values.social}
                    onChange={formik.handleChange}
                    error={formik.errors.social}
                    textError={formik.errors.social}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("assigned.views.total_invertion")}
                    span={t("general.mandatory")}
                    id="inversion"
                    name="inversion"
                    type="number"
                    placeholder={t("assigned.views.total_invertion")}
                    value={formik.values.inversion}
                    onChange={formik.handleChange}
                    error={formik.errors.inversion}
                    textError={formik.errors.inversion}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("assigned.views.clv")}
                    span="Obligatorio"
                    id="clv"
                    name="clv"
                    type="number"
                    placeholder={t("assigned.views.clv").slice(0, 30)}
                    value={formik.values.clv}
                    onChange={formik.handleChange}
                    error={formik.errors.clv}
                    textError={formik.errors.clv}
                  />
                </div>
                <div className="col-span-full">
                  <p className="block text-sm font-medium leading-6 text-gray-600">
                    {t("assigned.views.camps_personalizates")}
                  </p>
                  <div className="relative flex items-start mt-3">
                    <div className="flex h-6 items-center">
                      <input
                        checked={formik.values.sex}
                        id={"sex"}
                        name={"sex"}
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-indigo-600"
                        onChange={formik.handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm leading-6">
                      <label
                        htmlFor={"sex"}
                        className="font-medium text-gray-900"
                      >
                        Sexo
                      </label>
                      <p className="text-gray-500">Incluir campo de sexo</p>
                    </div>
                  </div>
                  <div className="relative flex items-start mt-3">
                    <div className="flex h-6 items-center">
                      <input
                        checked={formik.values.birthdate}
                        id={"birthdate"}
                        name={"birthdate"}
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-indigo-600"
                        onChange={formik.handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm leading-6">
                      <label
                        htmlFor={"birthdate"}
                        className="font-medium text-gray-900"
                      >
                        Fecha de nacimiento
                      </label>
                      <p className="text-gray-500">
                        Incluir campo de fecha de nacimiento
                      </p>
                    </div>
                  </div>

                  <div className="relative flex items-start mt-3">
                    <div className="flex h-6 items-center">
                      <input
                        checked={formik.values.phone}
                        id={"phone"}
                        name={"phone"}
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-indigo-600"
                        onChange={formik.handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm leading-6">
                      <label
                        htmlFor={"phone"}
                        className="font-medium text-gray-900"
                      >
                        Número de Whatsapp / Móvil
                      </label>
                      <p className="text-gray-500">
                        Incluir campo de número de contacto
                      </p>
                    </div>
                  </div>

                  <div className="relative flex items-start mt-3">
                    <div className="flex h-6 items-center">
                      <input
                        checked={formik.values.comments}
                        id={"comments"}
                        name={"comments"}
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-indigo-600"
                        onChange={formik.handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm leading-6">
                      <label
                        htmlFor={"comments"}
                        className="font-medium text-gray-900"
                      >
                        Comentarios
                      </label>
                      <p className="text-gray-500">
                        Incluir campo de comentarios
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogCreate>
    </Page>
  );
};
